import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, RefreshCw, Save } from "lucide-react";
import { api } from "@/lib/api";

const GT_TYPES = [
  { k: "lower_third", l: "Lower Thirds", w: 1920, h: 1080 },
  { k: "transparent_graphic", l: "Transparent Graphics", w: 1080, h: 1080 },
];

function ScorecardManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Cricket");

  const load = async () => {
    setLoading(true);
    try { setItems(await api.igAdminScorecards()); } catch { toast.error("Could not load scorecards."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Enter a template name."); return; }
    try { await api.igAdminCreateScorecard({ name: name.trim(), category }); setName(""); toast.success("Scorecard added"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggle = async (t) => { try { await api.igAdminUpdateScorecard(t.id, { name: t.name, enabled: !t.enabled }); setItems((x) => x.map((v) => (v.id === t.id ? { ...v, enabled: !v.enabled } : v))); } catch { toast.error("Failed"); } };
  const del = async (t) => { if (!window.confirm(`Delete "${t.name}"?`)) return; try { await api.igAdminDeleteScorecard(t.id); setItems((x) => x.filter((v) => v.id !== t.id)); toast.success("Deleted"); } catch { toast.error("Failed"); } };

  return (
    <div data-testid="lsg-scorecards">
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#E1306C]/30 bg-white/[0.02] p-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Scorecard template name" data-testid="lsg-name"
          className="flex-1 rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-sm text-white outline-none" />
        <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category"
          className="w-32 rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-sm text-white outline-none" />
        <button onClick={create} data-testid="lsg-create" className="flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-3 py-1.5 text-xs font-bold text-white"><Plus size={14} /> Add</button>
      </div>
      {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#E1306C]" /></div> : (
        <div className="space-y-2" data-testid="lsg-list">
          {items.map((t) => (
            <div key={t.id} data-testid={`lsg-item-${t.id}`} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3">
              <div className="flex-1">
                <div className="text-sm font-bold text-white">{t.name}</div>
                <div className="text-[11px] text-[#666]">{t.category} · {t.width}×{t.height} · Transparent PNG · {(t.fields || []).length} fields</div>
              </div>
              <button onClick={() => toggle(t)} data-testid={`lsg-toggle-${t.id}`} className={`rounded px-2 py-1 text-[10px] font-bold ${t.enabled ? "bg-[#22c55e]/15 text-[#22c55e]" : "bg-white/10 text-[#A1A1AA]"}`}>{t.enabled ? "Enabled" : "Disabled"}</button>
              <button onClick={() => del(t)} data-testid={`lsg-delete-${t.id}`} className="rounded p-1 text-[#A1A1AA] hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GraphicTemplateManager({ type }) {
  const cfg = GT_TYPES.find((t) => t.k === type);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("General");
  const [editing, setEditing] = useState(null); // {id, json}

  const load = async () => {
    setLoading(true);
    try { setItems(await api.igAdminGraphicTemplates(type)); } catch { toast.error("Could not load templates."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [type]); // eslint-disable-line

  const create = async () => {
    if (!name.trim()) { toast.error("Enter a template name."); return; }
    try { await api.igAdminCreateGraphicTemplate({ name: name.trim(), output_type: type, category, width: cfg.w, height: cfg.h }); setName(""); toast.success("Template added"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggle = async (t) => { try { await api.igAdminUpdateGraphicTemplate(t.id, { name: t.name, output_type: type, enabled: !t.enabled }); setItems((x) => x.map((v) => (v.id === t.id ? { ...v, enabled: !v.enabled } : v))); } catch { toast.error("Failed"); } };
  const del = async (t) => { if (!window.confirm(`Delete "${t.name}"?`)) return; try { await api.igAdminDeleteGraphicTemplate(t.id); setItems((x) => x.filter((v) => v.id !== t.id)); toast.success("Deleted"); } catch { toast.error("Failed"); } };
  const saveLayers = async (t) => {
    let layers;
    try { layers = JSON.parse(editing.json); if (!Array.isArray(layers)) throw new Error(); }
    catch { toast.error("Layers must be a valid JSON array."); return; }
    try { await api.igAdminUpdateGraphicTemplate(t.id, { name: t.name, output_type: type, layers }); toast.success("Layers saved"); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid={`gt-manager-${type}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#8B5CF6]/30 bg-white/[0.02] p-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={`${cfg.l} template name`} data-testid={`gt-name-${type}`}
          className="flex-1 rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-sm text-white outline-none" />
        <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category"
          className="w-32 rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-sm text-white outline-none" />
        <button onClick={create} data-testid={`gt-create-${type}`} className="flex items-center gap-1.5 rounded-lg bg-[#8B5CF6] px-3 py-1.5 text-xs font-bold text-white"><Plus size={14} /> Add ({cfg.w}×{cfg.h})</button>
      </div>
      {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#8B5CF6]" /></div> : (
        <div className="space-y-2" data-testid={`gt-list-${type}`}>
          {items.length === 0 && <div className="py-6 text-center text-xs text-[#666]">No templates yet.</div>}
          {items.map((t) => (
            <div key={t.id} data-testid={`gt-item-${t.id}`} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="text-sm font-bold text-white">{t.name}</div>
                  <div className="text-[11px] text-[#666]">{t.category} · {t.width}×{t.height} · {(t.layers || []).length} elements · Transparent PNG</div>
                </div>
                <button onClick={() => setEditing(editing?.id === t.id ? null : { id: t.id, json: JSON.stringify(t.layers || [], null, 2) })} className="rounded border border-white/15 px-2 py-1 text-[10px] font-bold text-white">Edit layers</button>
                <button onClick={() => toggle(t)} data-testid={`gt-toggle-${t.id}`} className={`rounded px-2 py-1 text-[10px] font-bold ${t.enabled ? "bg-[#22c55e]/15 text-[#22c55e]" : "bg-white/10 text-[#A1A1AA]"}`}>{t.enabled ? "Enabled" : "Disabled"}</button>
                <button onClick={() => del(t)} data-testid={`gt-delete-${t.id}`} className="rounded p-1 text-[#A1A1AA] hover:text-red-400"><Trash2 size={14} /></button>
              </div>
              {editing?.id === t.id && (
                <div className="mt-2">
                  <div className="mb-1 text-[10px] text-[#A1A1AA]">Layer definitions (JSON array). Each: type text/rect/image, x, y, width, height, fill, fontSize, etc. Canvas is transparent.</div>
                  <textarea value={editing.json} onChange={(e) => setEditing((s) => ({ ...s, json: e.target.value }))} rows={8} data-testid={`gt-layers-${t.id}`}
                    className="w-full rounded border border-white/10 bg-black/40 px-2 py-1 font-mono text-[11px] text-white outline-none" />
                  <button onClick={() => saveLayers(t)} data-testid={`gt-save-${t.id}`} className="mt-1.5 flex items-center gap-1.5 rounded bg-[#22c55e] px-3 py-1.5 text-xs font-bold text-black"><Save size={12} /> Save Layers</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LiveStreamGraphics() {
  const [sub, setSub] = useState("scorecard");
  const SUBS = [["scorecard", "Scorecards"], ["lower_third", "Lower Thirds"], ["transparent_graphic", "Transparent Graphics"]];

  return (
    <div data-testid="live-stream-graphics">
      <div className="mb-4">
        <h2 className="font-display text-lg font-black text-white">Live Stream Graphics</h2>
        <p className="text-xs text-[#A1A1AA]">Structured overlays available in the V2 editor. Users export them as transparent PNGs for OBS/vMix/Streamlabs. Enable/disable each output type in Feature Control.</p>
      </div>
      <div className="mb-4 flex gap-2" data-testid="lsg-subtabs">
        {SUBS.map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`lsg-subtab-${k}`}
            className={`rounded-lg px-3 py-1.5 text-xs font-bold ${sub === k ? "bg-[#E1306C] text-white" : "border border-white/10 text-[#A1A1AA] hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {sub === "scorecard" && <ScorecardManager />}
      {sub === "lower_third" && <GraphicTemplateManager type="lower_third" />}
      {sub === "transparent_graphic" && <GraphicTemplateManager type="transparent_graphic" />}
    </div>
  );
}
